"use client";

import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { InboxIcon, Phone1Icon, PinLocationIcon } from "@/components/icons/assets";

const contactInfo = [
  {
    icon: Phone1Icon,
    label: "Phone Number",
    value: "+34 687 914 501",
  },
  {
    icon: InboxIcon,
    label: "Email",
    value: "sales@guniwheel.com",
  },
  {
    icon: PinLocationIcon,
    label: "Address",
    value: "Gladsaxe Møllevej 21, 2860 Søborg",
  },
] as const;

export default function ContactSection() {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    subject: "",
    address: "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log(formData);
  };

  return (
    <section
      id="contact-section"
      className="bg-background pt-32 xl:pt-[190px] pb-[120px] px-4 sm:px-6 lg:px-10"
    >
      <div className="w-full max-w-[1440px] mx-auto">
        <div className="w-full flex flex-col lg:flex-row items-start justify-between gap-10 xl:gap-0 xl:h-[618px]">

          {/* ── Left Side ── */}
          <div className="flex flex-col justify-between gap-10 lg:gap-0 w-full xl:w-[710px] xl:h-[618px]">

            {/* Heading + Description */}
            <div className="flex flex-col gap-[24px] w-full xl:h-[234px]">
              <h2 className="font-oswald font-medium text-[32px] sm:text-[42px] xl:text-[60px] leading-[1.2] xl:leading-[80px] tracking-[0.2px] uppercase text-neutral-500 w-full xl:h-[160px]">
                Get in Touch With the Guniwheel™ Team
              </h2>
              <p className="font-roboto font-normal text-[14px] sm:text-[16px] xl:text-[18px] leading-[25px] text-neutral-300 w-full xl:h-[50px]">
                We're here to help with product questions, fitment guidance, orders, or support. Fill out the form and our team will get back to you shortly.
              </p>
            </div>

            {/* Contact Info */}
            <div className="flex flex-col gap-6 xl:gap-[40px] w-full xl:w-[281px] xl:h-[281px]">
              {contactInfo.map(({ icon: Icon, label, value }) => (
                <div key={label} className="flex flex-col gap-[12px]">
                  <div className="flex items-center gap-[10px]">
                    <Icon aria-hidden="true" />
                    <span className="font-oswald font-normal text-[18px] xl:text-[20px] leading-[100%] tracking-[-0.02em] text-neutral-500">
                      {label}
                    </span>
                  </div>
                  <p className="font-roboto font-normal text-[14px] xl:text-[18px] leading-[25px] text-neutral-300">
                    {value}
                  </p>
                </div>
              ))}
            </div>

          </div>

          {/* ── Right Side — Form Card ── */}
          <div className="w-full lg:w-[603px] h-auto xl:h-[618px] bg-background-50 rounded-[12px] flex flex-col justify-between gap-6 xl:gap-0 py-[30px]">

            {/* Fields */}
            <div className="flex flex-col gap-[20px] px-4 sm:px-[30px]">

              {/* Full Name */}
              <div className="flex flex-col gap-[12px]">
                <label
                  htmlFor="fullName"
                  className="font-oswald font-medium text-[14px] xl:text-[16px] leading-[100%] tracking-[0.2px] uppercase text-neutral-500"
                >
                  Full Name <span className="text-primary-500">*</span>
                </label>
                <Input
                  id="fullName"
                  type="text"
                  placeholder="Enter your full name"
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  required
                  className="font-roboto bg-background border-none shadow-none h-[46px] rounded-[10px] text-neutral-300 text-[14px] xl:text-[16px]"
                />
              </div>

              {/* Email Address */}
              <div className="flex flex-col gap-[12px]">
                <label
                  htmlFor="email"
                  className="font-oswald font-medium text-[14px] xl:text-[16px] leading-[100%] tracking-[0.2px] uppercase text-neutral-500"
                >
                  Email Address <span className="text-primary-500">*</span>
                </label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email address"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="font-roboto bg-background border-none shadow-none h-[46px] rounded-[10px] text-neutral-300 text-[14px] xl:text-[16px]"
                />
              </div>

              {/* Subject */}
              <div className="flex flex-col gap-[12px]">
                <label
                  htmlFor="subject"
                  className="font-oswald font-medium text-[14px] xl:text-[16px] leading-[100%] tracking-[0.2px] uppercase text-neutral-500"
                >
                  Subject
                </label>
                <Input
                  id="subject"
                  type="text"
                  placeholder="Enter subject"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  className="font-roboto bg-background border-none shadow-none h-[46px] rounded-[10px] text-neutral-300 text-[14px] xl:text-[16px]"
                />
              </div>

              {/* Address */}
              <div className="flex flex-col gap-[12px]">
                <label
                  htmlFor="address"
                  className="font-oswald font-medium text-[14px] xl:text-[16px] leading-[100%] tracking-[0.2px] uppercase text-neutral-500"
                >
                  Address <span className="text-primary-500">*</span>
                </label>
                <Textarea
                  id="address"
                  placeholder="Enter your address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  required
                  className="font-roboto bg-background border-none shadow-none rounded-[10px] text-neutral-300 text-[14px] xl:text-[16px] resize-none h-[120px] xl:h-[96px]"
                />
              </div>

            </div>

            {/* Submit Button */}
            <div className="flex justify-end px-4 sm:px-[30px]">
              <Button
                type="submit"
                onClick={handleSubmit}
                className="w-full sm:w-[181px] h-[60px] px-[24px] py-[10px] rounded-[10px] border-b-2 border-primary-600 bg-primary-500 hover:bg-primary-600 font-oswald font-medium text-[20px] leading-[100%] tracking-[0.2px] uppercase text-white cursor-pointer shadow-none"
              >
                SUBMIT INQUIRY
              </Button>
            </div>

          </div>

        </div>
      </div>
    </section>
  );
}